import dayjs from 'dayjs'
import ical from 'ical-generator'

import { orderStatuses, deliveryStatuses } from '@jarvis.oddle/core/tools/order'

import { isObjectEmpty } from 'src/utils/object'
import IconCancelledOrder from 'src/components/icons/IconNewCancelledOrder'
import IconConfirmedOrder from 'src/components/icons/IconNewConfirmedOrder'
import IconFulfilledOrder from 'src/components/icons/IconNewFulfilledOrder'
import IconPickupOrder from 'src/components/icons/IconNewPickupOrder'
import IconPreparingOrder from 'src/components/icons/IconNewPreparingOrder'
import IconPlacedOrder from 'src/components/icons/IconNewPlacedOrder'
import IconDeliveringOrder from 'src/components/icons/IconDeliveringOrder'
import { getMonthFromShortNameOfMonthToNumber, parseDateString } from 'src/utils/date'

export interface OrderStatusDescription {
  Icon: React.ComponentClass<{
    width: number
    height: number
  }>
  header: string
  description: string
  status: string
  showTrackingMap: boolean
  ETA: string
  trackingMessage: string
  lateNotification: {
    header: string
    content: string
  } | null
  isLookingForDriver: boolean
}

export const listOfPickupOrder = [
  'Received',
  'Confirmed',
  'Preparing',
  'Ready',
  'Collected'
]

export const listOfDeliveryStatus = [
  'Received',
  'Confirmed',
  'Preparing',
  'Delivering',
  'Delivered'
]

export const deliverySteps = {
  Loading: 0,
  Received: 1,
  Confirmed: 2,
  Preparing: 3,
  Delivering: 4,
  Delivered: 5
}

export const pickupSteps = {
  Loading: 0,
  Received: 1,
  Confirmed: 2,
  Preparing: 3,
  Ready: 4,
  Collected: 5
}

const statusText = {
  received: 'Received',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  collected: 'Collected',
  cancelled: 'Cancelled',
  delivering: 'Delivering',
  delivered: 'Delivered'
}

interface CreateCalendarEvent {
  storeOrder: Oms.V4.StoreOrder
  option?: string
  publicUrl?: string
  t?: (text: string) => string
}

export const createCalendarEvent = ({
  storeOrder,
  option,
  publicUrl,
  t
}: CreateCalendarEvent): string => {
  const {
    collectionDate,
    collectionTime,
    isPickup,
    storeAddress,
    orderNumber,
    deliveryAddress
  } = storeOrder
  const address = isPickup ? storeAddress : deliveryAddress
  const description = `${t('Track your order at: ')}${
    publicUrl || window.location.href
  }`
  const summary = isPickup
    ? `${t('Pickup order')} #${orderNumber}`
    : `${t('Delivery order')} #${orderNumber}`
  const collectionTimeSplit = collectionTime.split('to')
  const [date, month, year] = collectionDate.split(' ')
  const [hours, minutes] = dayjs(`${collectionDate} ${collectionTimeSplit[0]}`)
    .format('HH:mm')
    .split(':')
  const monthInNumber = getMonthFromShortNameOfMonthToNumber(month)

  const eventStartTime = new Date()
  eventStartTime.setFullYear(Number(year))
  eventStartTime.setMonth(monthInNumber)
  eventStartTime.setDate(Number(date))
  eventStartTime.setHours(Number(hours))
  eventStartTime.setMinutes(Number(minutes))

  const [endOfEventTimeInHours, endOfEventTimeInMinutes] = dayjs(
    `${collectionDate} ${collectionTime.split('to')[1]}`
  )
    .format('HH:mm')
    .split(':')
  const eventEndTime = new Date()
  eventEndTime.setFullYear(Number(year))
  eventEndTime.setMonth(monthInNumber)
  eventEndTime.setDate(Number(date))
  eventEndTime.setHours(Number(endOfEventTimeInHours))
  eventEndTime.setMinutes(Number(endOfEventTimeInMinutes))

  if (option === 'google calendar') {
    return createGoogleCalendar({
      address,
      description,
      eventStartTime,
      eventEndTime,
      orderNumber,
      isPickup,
      publicUrl,
      t
    })
  }

  const calendar = ical({ name: 'Reminder Order' })
  calendar
    .createEvent({
      start: eventStartTime,
      end: eventEndTime,
      summary,
      description,
      location: address || '',
      url: publicUrl || window.location.href
    })
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    .createAlarm({ type: 'display', trigger: 600 })

  const data: File = new File([calendar.toString() as string], 'calendar.ics', {
    type: 'text/plain'
  })

  return window.URL.createObjectURL(data)
}

interface CreateGoogleCalendar {
  address: string
  description: string
  eventStartTime: Date
  eventEndTime: Date
  orderNumber: string
  isPickup: boolean
  publicUrl: string
  t?: (text: string) => string
}

const createGoogleCalendar = ({
  address,
  eventStartTime,
  eventEndTime,
  orderNumber,
  isPickup,
  publicUrl,
  t
}: CreateGoogleCalendar): string => {
  // when share url via addThis, these special charaters are appear (# and .) and cause the token being mixed with these special charaters
  // need to remove everything after these special charaters (# and .) from the url to prevent show the wrong token in url
  const shareUrl = (publicUrl || window.location.href).replace(/#.*/g, '')
  const convertTimeToIso = (eventTime: Date): string => {
    const event = eventTime
      .toISOString()
      .replaceAll('-', '')
      .replaceAll(':', '')
      .split('.')[0]
    return `${event}0Z`
  }

  return `https://calendar.google.com/calendar/u/0/r/eventedit?dates=${convertTimeToIso(
    eventStartTime
  )}/${convertTimeToIso(eventEndTime)}&location=${
    address ? address.replace(/[&/\\#+()$~%'":*?<>{}]/g, '') : ''
  }&details=${encodeURI(t('Track your order at '))}${shareUrl}&text=${
    isPickup ? encodeURI(t('Pickup order')) : encodeURI(t('Delivery order'))
  }%20%23${orderNumber}`
}

const initialResult: OrderStatusDescription = {
  Icon: null,
  header: '',
  description: '',
  status: '',
  showTrackingMap: false,
  ETA: '',
  trackingMessage: '',
  lateNotification: null,
  isLookingForDriver: false
}

const getPickupOrderStatusDescription = ({ t, storeOrder, collectionWindowInMs }): OrderStatusDescription => {
  const { status } = storeOrder
  const result = { ...initialResult }
  const currentTime = new Date().getTime()
  // 15 minutes before the collection window
  const startOfPreparingFoodInMs = collectionWindowInMs - 15 * 60 * 1000

  switch (status) {
    case orderStatuses.pending:
      result.header = t('Received your order.')
      result.description = t(
        'We are currently reviewing your order, this may take a few moments...'
      )
      result.status = statusText.received
      result.showTrackingMap = false
      result.ETA = 'Pickup window'
      result.Icon = IconPlacedOrder
      result.trackingMessage = ''
      return result
    case orderStatuses.confirmed:
      if (currentTime < startOfPreparingFoodInMs) {
        result.header = t('Order Confirmed')
        result.description = t(
          'Yes! We have accepted your order and we will start preparing your order at a closer time.'
        )
        result.status = statusText.confirmed
        result.showTrackingMap = false
        result.ETA = 'Pickup window'
        result.Icon = IconConfirmedOrder
        result.trackingMessage = ''
        return result
      }

      if (
        currentTime >= startOfPreparingFoodInMs &&
        currentTime <= collectionWindowInMs
      ) {
        result.header = t('Preparing your order...')
        result.description = t(
          "It's happening! We are preparing your order now, stay tuned for updates."
        )
        result.status = statusText.preparing
        result.showTrackingMap = false
        result.ETA = 'Pickup window'
        result.Icon = IconPreparingOrder
        result.trackingMessage = ''
        return result
      }

      // currentTime > collectionWindowInMs
      result.header = t('Ready for pick up!')
      result.description = t(
        "We're ready for you! Your order is ready for pickup."
      )
      result.status = statusText.ready
      result.showTrackingMap = false
      result.ETA = 'Pickup window'
      result.Icon = IconPickupOrder
      result.trackingMessage = ''
      return result
    case orderStatuses.fulfilled:
      result.header = t('Enjoy your meal!')
      result.description = t('Thanks for ordering with us. See you again!')
      result.status = statusText.collected
      result.showTrackingMap = false
      result.ETA = 'Pickup window'
      result.Icon = IconFulfilledOrder
      result.trackingMessage = ''
      return result
    case orderStatuses.cancelled:
      result.header = t('Your order has been cancelled.')
      result.description = t(
        "We're sorry for the inconvenience, and we hope to serve you again soon!"
      )
      result.status = statusText.cancelled
      result.showTrackingMap = false
      result.ETA = ''
      result.Icon = IconCancelledOrder
      result.trackingMessage = ''
      return result
    default:
      return result
  }
}

const getDeliveryOrderStatusDescription = ({ t, storeOrder, startOfCollectionWindowInMs, endCollectionWindowInMs, etaToCustomer }): OrderStatusDescription => {
  const currentTime = new Date().getTime()
  const result = { ...initialResult }
  const { status, deliveryRequestDto } = storeOrder
  const listOfDriverOnTheWayStatuses = [
    deliveryStatuses.driverOnTheWay,
    deliveryStatuses.driverDelivered
  ]
  const isFulfilledByOddleLogistics =
    ((deliveryRequestDto || {}).fulfilledByOddleLogistics as boolean) || false

  // 15 minutes before driver pick up the food
  // for example collection time is 06:00 pm
  // driver should pick up the food at 05:30 pm
  // before pickup the food, we need to set preparing time, where 15 minutes before 05:30 pm is 05:15 pm to 05:30 pm
  const startOfPreparingFoodInMs = startOfCollectionWindowInMs - 15 * 60 * 1000
  const triggerCustomerDelayNotification = endCollectionWindowInMs - 900000

  const isDriverAssigned =
    !isObjectEmpty(deliveryRequestDto) &&
    deliveryRequestDto.status === deliveryStatuses.assigned
  const isDriverNotAssigned =
    !isObjectEmpty(deliveryRequestDto) &&
    !isDriverAssigned &&
    !listOfDriverOnTheWayStatuses.includes(deliveryRequestDto.status)
  const isDriverOnTheWay =
    !isObjectEmpty(deliveryRequestDto) &&
    listOfDriverOnTheWayStatuses.includes(deliveryRequestDto.status)

  // condition are made based on -> https://docs.google.com/spreadsheets/d/1J3OQmeNUH_f6VqFyTI-oWQEUBnmqY3rkxTDHvRfLzbs/edit#gid=623665781
  switch (status) {
    case orderStatuses.pending:
      result.header = t('Received your order.')
      result.description = t(
        'We are currently reviewing your order, this may take a few moments...'
      )
      result.status = statusText.received
      result.showTrackingMap = false
      result.ETA = 'Delivery window'
      result.Icon = IconPlacedOrder
      result.trackingMessage = ''
      return result
    case orderStatuses.confirmed:
      // Status order is confirmed
      // Delivery Request = no
      // Driver Assigned = no
      if (isObjectEmpty(deliveryRequestDto)) {
        if (currentTime < startOfPreparingFoodInMs) {
          result.header = t('Order Confirmed')
          result.description = t(
            'Yes! We have accepted your order and we will start preparing your order at a closer time.'
          )
          result.status = statusText.confirmed
          result.showTrackingMap = false
          result.ETA = 'Delivery window'
          result.Icon = IconConfirmedOrder
          result.trackingMessage = t(
            'Live tracking and arrival estimate may not be available for this order'
          )
          return result
        }

        if (
          currentTime >= startOfPreparingFoodInMs &&
          currentTime <= startOfCollectionWindowInMs
        ) {
          result.header = t('Preparing your order...')
          result.description = t(
            "It's happening! We are preparing your order now, stay tuned for updates."
          )
          result.status = statusText.preparing
          result.showTrackingMap = false
          result.ETA = 'Delivery window'
          result.Icon = IconPreparingOrder
          result.trackingMessage = t(
            'Live tracking and arrival estimate may not be available for this order'
          )
          return result
        }
        // after 72 hours with endCollectionWindowInMs but the status of order still is confirmed
        if (currentTime - endCollectionWindowInMs >= 72 * 60 * 60000) {
          result.header = t('Enjoy your meal!')
          result.description = t('Thanks for ordering with us. See you again!')
          result.status = statusText.delivered
          result.showTrackingMap = false
          result.ETA = ''
          result.Icon = IconFulfilledOrder
          result.trackingMessage = ''
          return result
        }
        // currentTime > collectionWindowInMs
        result.header = t('On the way')
        result.description = t(
          "Sit tight! Your order is on its way to you and we will let you know when it's delivered."
        )
        result.status = statusText.delivering
        result.showTrackingMap = false
        result.ETA = 'Delivery window'
        result.Icon = IconDeliveringOrder
        result.trackingMessage = t(
          'Live tracking and arrival estimate may not be available for this order'
        )
        return result
      }

      // Status order is confirmed
      // Delivery Request = yes
      // Driver Assigned = no
      if (!isObjectEmpty(deliveryRequestDto) && isDriverNotAssigned) {
        if (currentTime < startOfPreparingFoodInMs) {
          result.header = t('Order Confirmed')
          result.description = t(
            'Yes! We have accepted your order and we will start preparing your order at a closer time.'
          )
          result.status = statusText.confirmed
          result.showTrackingMap = false
          result.ETA = 'Delivery window'
          result.Icon = IconConfirmedOrder
          result.trackingMessage = ''
          return result
        }

        if (currentTime >= startOfPreparingFoodInMs) {
          result.header = t('Preparing your order...')
          result.description = t(
            "It's happening! We are preparing your order now, stay tuned for updates."
          )
          result.status = statusText.preparing
          result.showTrackingMap = false
          result.ETA = 'Looking for driver'
          result.Icon = IconPreparingOrder
          result.trackingMessage = ''
          // Late notification should only be triggered 15min from end of delivery window.
          if (currentTime >= triggerCustomerDelayNotification) {
            result.lateNotification = getCustomerLateNotification({
              ETA: 'Looking for driver',
              isFulfilledByOddleLogistics,
              t
            })
          }
          result.isLookingForDriver = true
          return result
        }
      }

      // Status order is confirmed
      // Delivery Request = yes
      // Driver Assigned = yes
      if (!isObjectEmpty(deliveryRequestDto)) {
        // If delivery request is made and driver assigned, delivery order status should
        // remain at Preparing until delivery request status is changed to Picked Up
        // (Driver on the way).

        // (Note: Delivering order status refers to the delivery of food from store to
        // customer, so status should remain as Preparing if driver has not picked up food)
        if (isDriverAssigned) {
          if (currentTime < startOfPreparingFoodInMs) {
            result.header = t('Order Confirmed')
            result.description = t(
              'Yes! We have accepted your order and we will start preparing your order at a closer time.'
            )
            result.status = statusText.confirmed
            result.showTrackingMap = false
            result.ETA = 'Delivery window'
            result.Icon = IconConfirmedOrder
            result.trackingMessage = ''
            return result
          }

          result.header = t('Preparing your order...')
          result.description = t(
            "It's happening! We are preparing your order now, stay tuned for updates."
          )
          result.status = statusText.preparing
          result.showTrackingMap = true
          result.ETA = t('Live')
          result.Icon = IconPreparingOrder
          result.trackingMessage = ''
          return result
        }

        if (isDriverOnTheWay) {
          result.header = t('On the way')
          result.description = t(
            "Sit tight! Your order is on its way to you and we will let you know when it's delivered."
          )
          result.status = statusText.delivering
          result.showTrackingMap = true
          result.ETA = t('Live')
          result.Icon = IconDeliveringOrder
          result.trackingMessage = ''
          if (currentTime >= triggerCustomerDelayNotification && etaToCustomer) {
            if (currentTime + etaToCustomer > endCollectionWindowInMs) {
              result.lateNotification = getCustomerLateNotification({
                ETA: 'Live',
                isFulfilledByOddleLogistics,
                t
              })
            }
          }
          return result
        }
      }
      break
    case orderStatuses.fulfilled:
      // Merchant set “Fulfilled” as a means to sort out the orders that they have prepared (Currently all under “Confirmed”)
      // so that they can focus on the orders they have to prepare.
      // Delivery Request = yes
      // Driver Assigned = yes
      if (deliveryRequestDto) {
        if (deliveryRequestDto.status <= deliveryStatuses.assigned) {
          if (currentTime < startOfPreparingFoodInMs) {
            result.header = t('Order Confirmed')
            result.description = t(
              'Yes! We have accepted your order and we will start preparing your order at a closer time.'
            )
            result.status = statusText.confirmed
            result.showTrackingMap = false
            result.ETA = 'Delivery window'
            result.Icon = IconConfirmedOrder
            result.trackingMessage = ''
            return result
          }

          result.header = t('Preparing your order...')
          result.description = t(
            "It's happening! We are preparing your order now, stay tuned for updates."
          )
          result.status = statusText.preparing
          result.showTrackingMap = true
          result.ETA = t('Live')
          result.Icon = IconPreparingOrder
          result.trackingMessage = ''
          return result
        }
        if (deliveryRequestDto.status === deliveryStatuses.driverOnTheWay) {
          result.header = t('On the way')
          result.description = t(
            "Sit tight! Your order is on its way to you and we will let you know when it's delivered."
          )
          result.status = statusText.delivering
          result.showTrackingMap = true
          result.ETA = t('Live')
          result.Icon = IconDeliveringOrder
          result.trackingMessage = ''
          if (currentTime >= triggerCustomerDelayNotification && etaToCustomer && currentTime + etaToCustomer > endCollectionWindowInMs) {
            result.lateNotification = getCustomerLateNotification({
              ETA: 'Live',
              isFulfilledByOddleLogistics,
              t
            })
          }
          return result
        }
      }
      result.header = t('Enjoy your meal!')
      result.description = t('Thanks for ordering with us. See you again!')
      result.status = statusText.delivered
      result.showTrackingMap = false
      result.ETA = ''
      result.Icon = IconFulfilledOrder
      result.trackingMessage = ''
      return result
    case orderStatuses.cancelled:
      result.header = t('Your order has been cancelled.')
      result.description = t(
        "We're sorry for the inconvenience, and we hope to serve you again soon!"
      )
      result.status = statusText.cancelled
      result.showTrackingMap = false
      result.ETA = ''
      result.Icon = IconCancelledOrder
      result.trackingMessage = ''
      return result
    default:
      return result
  }
}

export const getOrderStatusDescription = ({ t, storeOrder, etaToCustomer }: {
  t: (x) => string
  storeOrder: Oms.V4.StoreOrder
  etaToCustomer: number
}): OrderStatusDescription => {
  const collectionWindow = `${storeOrder.collectionDate} ${
    storeOrder.collectionTime.split(' to ')[0]
  }`
  const collectionWindowInMs = parseDateString(
    collectionWindow,
    'DD MMM YYYY hh:mm A'
  ).getTime()
  const endCollectionWindow = `${storeOrder.collectionDate} ${
    storeOrder.collectionTime.split(' to ')[1]
  }`
  const endCollectionWindowInMs = parseDateString(
    endCollectionWindow,
    'DD MMM YYYY hh:mm A'
  ).getTime()

  // for delivery order, 30 minutes before the collection window
  const startOfCollectionWindowInMs = collectionWindowInMs - 30 * 60 * 1000

  if (storeOrder.isPickup) {
    return getPickupOrderStatusDescription({
      t,
      storeOrder,
      collectionWindowInMs
    })
  }

  return getDeliveryOrderStatusDescription({
    t,
    storeOrder,
    endCollectionWindowInMs,
    startOfCollectionWindowInMs,
    etaToCustomer
  })
}

export const getDeliveryFulfilmentParty = ({ t, storeOrder }: {
  t: (x) => string
  storeOrder: Oms.V4.StoreOrder
}): {
  deliveryParty: string
  partnerDisplayName: string
} => {
  const { deliveryRequestDto } = storeOrder

  const isFulfilledByOddleLogistics =
    ((deliveryRequestDto || {}).fulfilledByOddleLogistics as boolean) || false

  if (isFulfilledByOddleLogistics) {
    const { partnerRequestDtos, trackingLink } = deliveryRequestDto
    return {
      deliveryParty: 'Oddle Logistics',
      partnerDisplayName: partnerRequestDtos
        ? t(`${partnerRequestDtos[0].partnerDisplayName}`)
        : trackingLink.includes('lalamove')
        ? 'lalamove'
        : ''
    }
  }

  return {
    deliveryParty: t('The restaurant'),
    partnerDisplayName: ''
  }
}

const getCustomerLateNotification = ({ isFulfilledByOddleLogistics, ETA, t }) => {
  switch (ETA) {
    case 'Looking for driver':
      if (isFulfilledByOddleLogistics) {
        return {
          header: t('Your order may be delayed'),
          content: t(
            "We're sorry. It's taking longer than usual to find a driver due to high demand. The team at Oddle Logistics is currently working closely with our various partners to deliver your order as soon as possible!"
          )
        }
      }

      return {
        header: t('Your order may be delayed'),
        content: t(
          "It's taking longer than usual to find a driver due to high demand. We are working closely with our partners to deliver your order as soon as possible!"
        )
      }
    case 'Live':
      if (isFulfilledByOddleLogistics) {
        return {
          header: t('Your order may be delayed'),
          content: t(
            "We're sorry. Your order may take longer to arrive due to weather/traffic/preparation reasons. The team at Oddle Logistics is currently working closely our various partners and the restaurant to deliver your order as soon as possible!"
          )
        }
      }

      return {
        header: t('Your order may be delayed'),
        content: t(
          "We're sorry! Your order may take longer to arrive. This may be due to weather/traffic conditions, or we needed more time to prepare your order."
        )
      }
  }
}
